/**
 * Operations sources — production floor (Work Mode), inventory,
 * procurement and projects. Each block is gated to its own module.
 */

import type { DatasetBag, DefectLite, PurchaseOrderLite, TaskLite } from '../datasets';
import type { BreakdownDef, ForecastSourceDef, MetricDef, TableDef } from '../catalogTypes';
import type { SeriesPoint } from '../types';
import { todayIso } from '../series';
import {
  avg,
  dailyCount,
  dailySeries,
  dailyWeightedAvg,
  daysBetween,
  groupCount,
  groupSum,
  inWindow,
  monthlySeries,
  sum,
  titleCase,
  toSlices,
} from './helpers';

// ---------------------------------------------------------------------------
// Production floor
// ---------------------------------------------------------------------------

const OPS = 'manufacturing';

function stationName(bag: DatasetBag, id: string | null): string {
  if (!id) return 'No station';
  return bag.workstations?.find((w) => w.id === id)?.name ?? 'Unknown station';
}

function goodUnitsDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(bag.station_daily ?? [], (r) => r.day, (r) => r.units);
}

function scrapDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(bag.station_daily ?? [], (r) => r.day, (r) => r.scrapped);
}

function attemptsDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(bag.station_daily ?? [], (r) => r.day, (r) => r.units + r.scrapped);
}

function defectCost(d: DefectLite): number {
  return d.material_cost_cents + d.labor_cost_cents;
}

export const OPERATIONS_METRICS: MetricDef[] = [
  {
    id: 'ops.units_produced',
    kind: 'metric',
    label: 'Units produced',
    group: 'Operations',
    description: 'Good units completed at workstations.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: goodUnitsDaily,
    nonNegative: true,
  },
  {
    id: 'ops.units_scrapped',
    kind: 'metric',
    label: 'Units scrapped',
    group: 'Operations',
    description: 'Units logged as scrap at workstations.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: scrapDaily,
    nonNegative: true,
  },
  {
    id: 'ops.units_attempted',
    kind: 'metric',
    label: 'Units attempted',
    group: 'Operations',
    description: 'Good plus scrapped units — the denominator for yield.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: attemptsDaily,
    nonNegative: true,
  },
  {
    id: 'ops.first_pass_yield',
    kind: 'metric',
    label: 'First-pass yield',
    group: 'Operations',
    description: 'Good units as a share of all units attempted.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'ops.units_produced', den: 'ops.units_attempted' },
  },
  {
    id: 'ops.scrap_rate',
    kind: 'metric',
    label: 'Scrap rate',
    group: 'Operations',
    description: 'Scrapped units as a share of all units attempted.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'percent',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'ops.units_scrapped', den: 'ops.units_attempted' },
  },
  {
    id: 'ops.cycle_time',
    kind: 'metric',
    label: 'Average cycle time',
    group: 'Operations',
    description: 'Seconds per good unit, weighted by units built.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'seconds',
    direction: 'down',
    grain: 'day',
    agg: 'avg',
    series: (bag) =>
      dailyWeightedAvg(bag.station_daily ?? [], (r) => r.day, (r) => r.avg_cycle_seconds, (r) => r.units),
  },
  {
    id: 'ops.workers_active',
    kind: 'metric',
    label: 'People on the floor',
    group: 'Operations',
    description: 'Average number of station-worker pairs logging units per day.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'count',
    direction: 'neutral',
    grain: 'day',
    agg: 'avg',
    series: (bag) => dailySeries(bag.station_daily ?? [], (r) => r.day, (r) => r.workers),
  },
  {
    id: 'ops.units_per_worker_day',
    kind: 'metric',
    label: 'Units per worker-day',
    group: 'Operations',
    description: 'Good units divided by station-worker days — a simple labour productivity read.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'ratio',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.station_daily) return null;
      const rows = bag.station_daily.filter((r) => inWindow(r.day, start, end));
      const workerDays = sum(rows.map((r) => r.workers));
      return workerDays === 0 ? null : sum(rows.map((r) => r.units)) / workerDays;
    },
  },
  {
    id: 'ops.defects_reported',
    kind: 'metric',
    label: 'Defects reported',
    group: 'Operations',
    description: 'Defect reports filed from the floor.',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount(bag.defects ?? [], (d) => d.day),
    nonNegative: true,
  },
  {
    id: 'ops.critical_defects',
    kind: 'metric',
    label: 'Critical defects',
    group: 'Operations',
    description: 'Defect reports marked critical.',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount((bag.defects ?? []).filter((d) => d.severity === 'critical'), (d) => d.day),
    nonNegative: true,
  },
  {
    id: 'ops.open_defects',
    kind: 'metric',
    label: 'Open defects',
    group: 'Operations',
    description: 'Defect reports still awaiting review.',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.defects ? bag.defects.filter((d) => d.status === 'open').length : null),
  },
  {
    id: 'ops.defect_cost',
    kind: 'metric',
    label: 'Cost of quality',
    group: 'Operations',
    description: 'Material plus labour cost attached to defect reports.',
    module: OPS,
    requires: ['defects'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.defects ?? [], (d) => d.day, defectCost),
    nonNegative: true,
  },
  {
    id: 'ops.morale',
    kind: 'metric',
    label: 'Team mood',
    group: 'Operations',
    description: 'Average daily check-in mood on a 1–5 scale.',
    module: OPS,
    requires: ['morale'],
    unit: 'score',
    direction: 'up',
    grain: 'day',
    agg: 'avg',
    series: (bag) =>
      dailyWeightedAvg(bag.morale ?? [], (r) => r.day, (r) => (r.responses > 0 ? r.avg_mood : null), (r) => r.responses),
  },
  {
    id: 'ops.low_mood_share',
    kind: 'metric',
    label: 'Low-mood check-ins',
    group: 'Operations',
    description: 'Share of check-ins at 1 or 2 out of 5.',
    module: OPS,
    requires: ['morale'],
    unit: 'percent',
    direction: 'down',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.morale) return null;
      const rows = bag.morale.filter((r) => inWindow(r.day, start, end));
      const responses = sum(rows.map((r) => r.responses));
      return responses === 0 ? null : sum(rows.map((r) => r.low_count)) / responses;
    },
  },
  {
    id: 'ops.checkins',
    kind: 'metric',
    label: 'Mood check-ins',
    group: 'Operations',
    description: 'Number of daily check-ins submitted.',
    module: OPS,
    requires: ['morale'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.morale ?? [], (r) => r.day, (r) => r.responses),
    nonNegative: true,
  },
];

export const OPERATIONS_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'ops.units_by_station',
    kind: 'breakdown',
    label: 'Units by workstation',
    group: 'Operations',
    description: 'Good units per station in the window.',
    module: OPS,
    requires: ['station_daily', 'workstations'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          (bag.station_daily ?? []).filter((r) => inWindow(r.day, start, end)),
          (r) => stationName(bag, r.workstation_id),
          (r) => r.units,
        ),
      ),
  },
  {
    id: 'ops.scrap_by_station',
    kind: 'breakdown',
    label: 'Scrap by workstation',
    group: 'Operations',
    description: 'Scrapped units per station in the window.',
    module: OPS,
    requires: ['station_daily', 'workstations'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          (bag.station_daily ?? []).filter((r) => inWindow(r.day, start, end)),
          (r) => stationName(bag, r.workstation_id),
          (r) => r.scrapped,
        ),
      ),
  },
  {
    id: 'ops.defects_by_severity',
    kind: 'breakdown',
    label: 'Defects by severity',
    group: 'Operations',
    description: 'Minor / major / critical split of defect reports.',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(groupCount((bag.defects ?? []).filter((d) => inWindow(d.day, start, end)), (d) => titleCase(d.severity))),
  },
  {
    id: 'ops.defects_by_disposition',
    kind: 'breakdown',
    label: 'Defects by disposition',
    group: 'Operations',
    description: 'What happened to defective units — scrap, rework, hold, use as-is.',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupCount((bag.defects ?? []).filter((d) => inWindow(d.day, start, end)), (d) => titleCase(d.disposition)),
      ),
  },
  {
    id: 'ops.defects_by_station',
    kind: 'breakdown',
    label: 'Defects by workstation',
    group: 'Operations',
    description: 'Where defects are being reported.',
    module: OPS,
    requires: ['defects', 'workstations'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupCount((bag.defects ?? []).filter((d) => inWindow(d.day, start, end)), (d) => stationName(bag, d.workstation_id)),
      ),
  },
  {
    id: 'ops.scrap_by_tag',
    kind: 'breakdown',
    label: 'Scrap by cause',
    group: 'Operations',
    description: 'Scrap tags on defect reports (a report can carry several).',
    module: OPS,
    requires: ['defects'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupCount(
          (bag.defects ?? []).filter((d) => inWindow(d.day, start, end)).flatMap((d) => d.scrap_tags),
          (tag) => titleCase(tag),
        ),
      ),
  },
  {
    id: 'ops.defect_cost_by_station',
    kind: 'breakdown',
    label: 'Cost of quality by workstation',
    group: 'Operations',
    description: 'Material plus labour cost of defects per station.',
    module: OPS,
    requires: ['defects', 'workstations'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          (bag.defects ?? []).filter((d) => inWindow(d.day, start, end)),
          (d) => stationName(bag, d.workstation_id),
          defectCost,
        ),
      ),
  },
];

export const OPERATIONS_TABLES: TableDef[] = [
  {
    id: 'ops.station_summary',
    kind: 'table',
    label: 'Workstation summary',
    group: 'Operations',
    description: 'Output, scrap, yield and cycle time per station for the window.',
    module: OPS,
    requires: ['station_daily', 'workstations'],
    columns: [
      { key: 'station', label: 'Station' },
      { key: 'units', label: 'Units', unit: 'count' },
      { key: 'scrapped', label: 'Scrap', unit: 'count' },
      { key: 'yield', label: 'Yield', unit: 'percent' },
      { key: 'cycle', label: 'Cycle', unit: 'seconds' },
      { key: 'workers', label: 'Worker-days', unit: 'count' },
    ],
    defaultSort: { key: 'units', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { units: number; scrapped: number; cycleNum: number; cycleDen: number; workers: number }>();
      for (const r of (bag.station_daily ?? []).filter((x) => inWindow(x.day, start, end))) {
        const row = acc.get(r.workstation_id) ?? { units: 0, scrapped: 0, cycleNum: 0, cycleDen: 0, workers: 0 };
        row.units += r.units;
        row.scrapped += r.scrapped;
        row.workers += r.workers;
        if (r.avg_cycle_seconds != null && r.units > 0) {
          row.cycleNum += r.avg_cycle_seconds * r.units;
          row.cycleDen += r.units;
        }
        acc.set(r.workstation_id, row);
      }
      return Array.from(acc.entries()).map(([id, r]) => {
        const attempts = r.units + r.scrapped;
        return {
          key: id,
          cells: {
            station: stationName(bag, id),
            units: r.units,
            scrapped: r.scrapped,
            yield: attempts === 0 ? null : r.units / attempts,
            cycle: r.cycleDen === 0 ? null : r.cycleNum / r.cycleDen,
            workers: r.workers,
          },
        };
      });
    },
  },
  {
    id: 'ops.open_defects_table',
    kind: 'table',
    label: 'Open defects',
    group: 'Operations',
    description: 'Defect reports awaiting review, newest first.',
    module: OPS,
    requires: ['defects', 'workstations'],
    columns: [
      { key: 'day', label: 'Reported', date: true },
      { key: 'severity', label: 'Severity' },
      { key: 'station', label: 'Station' },
      { key: 'disposition', label: 'Disposition' },
      { key: 'description', label: 'Description' },
      { key: 'cost', label: 'Cost', unit: 'currency' },
    ],
    defaultColumns: ['day', 'severity', 'station', 'description', 'cost'],
    defaultSort: { key: 'day', dir: 'desc' },
    defaultLimit: 8,
    rows: (bag) =>
      (bag.defects ?? [])
        .filter((d) => d.status === 'open')
        .map((d) => ({
          key: d.id,
          href: '/mfg/scrap-log',
          cells: {
            day: d.day,
            severity: titleCase(d.severity),
            station: stationName(bag, d.workstation_id),
            disposition: titleCase(d.disposition),
            description: d.description,
            cost: defectCost(d),
          },
        })),
  },
  {
    id: 'ops.daily_output',
    kind: 'table',
    label: 'Daily output',
    group: 'Operations',
    description: 'Units, scrap and yield per day in the window.',
    module: OPS,
    requires: ['station_daily'],
    columns: [
      { key: 'day', label: 'Day', date: true },
      { key: 'units', label: 'Units', unit: 'count' },
      { key: 'scrapped', label: 'Scrap', unit: 'count' },
      { key: 'yield', label: 'Yield', unit: 'percent' },
      { key: 'workers', label: 'Worker-days', unit: 'count' },
    ],
    defaultSort: { key: 'day', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { units: number; scrapped: number; workers: number }>();
      for (const r of (bag.station_daily ?? []).filter((x) => inWindow(x.day, start, end))) {
        const row = acc.get(r.day) ?? { units: 0, scrapped: 0, workers: 0 };
        row.units += r.units;
        row.scrapped += r.scrapped;
        row.workers += r.workers;
        acc.set(r.day, row);
      }
      return Array.from(acc.entries()).map(([day, r]) => ({
        key: day,
        cells: {
          day,
          units: r.units,
          scrapped: r.scrapped,
          yield: r.units + r.scrapped === 0 ? null : r.units / (r.units + r.scrapped),
          workers: r.workers,
        },
      }));
    },
  },
];

export const OPERATIONS_FORECASTS: ForecastSourceDef[] = [
  {
    id: 'fc.units_produced',
    kind: 'forecast',
    label: 'Units produced',
    group: 'Operations',
    description: 'Monthly good units projected forward.',
    module: OPS,
    requires: ['station_daily'],
    unit: 'count',
    nonNegative: true,
    history: (bag) => monthlySeries(bag.station_daily ?? [], (r) => r.day, (r) => r.units),
  },
];

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

const INV = 'inventory';

interface ItemStock {
  itemId: string;
  sku: string;
  name: string;
  kind: string | null;
  qty: number;
  valueCents: number;
  reorderPoint: number | null;
  reorderQty: number | null;
  active: boolean;
}

function itemStock(bag: DatasetBag): ItemStock[] {
  const acc = new Map<string, ItemStock>();
  for (const level of bag.inventory ?? []) {
    const row = acc.get(level.item_id) ?? {
      itemId: level.item_id,
      sku: level.item?.sku ?? '—',
      name: level.item?.name ?? 'Unknown item',
      kind: level.item?.kind ?? null,
      qty: 0,
      valueCents: 0,
      reorderPoint: level.item?.reorder_point ?? null,
      reorderQty: level.item?.reorder_qty ?? null,
      active: level.item?.is_active !== false,
    };
    row.qty += level.quantity;
    row.valueCents += level.quantity * (level.item?.std_cost_cents ?? 0);
    acc.set(level.item_id, row);
  }
  return Array.from(acc.values()).filter((r) => r.active);
}

function belowReorder(stock: ItemStock[]): ItemStock[] {
  return stock.filter((r) => r.reorderPoint != null && r.reorderPoint > 0 && r.qty <= r.reorderPoint);
}

/** Average daily units sold per SKU over the window, from order lines. */
function dailyDemandBySku(bag: DatasetBag, start: string, end: string): Map<string, number> {
  const days = Math.max(1, (daysBetween(start, end) ?? 0) + 1);
  const units = new Map<string, number>();
  for (const order of bag.orders?.orders ?? []) {
    if (order.status === 'cancelled' || !inWindow(order.day, start, end)) continue;
    for (const line of order.lines) {
      if (!line.sku) continue;
      units.set(line.sku, (units.get(line.sku) ?? 0) + line.quantity);
    }
  }
  const out = new Map<string, number>();
  for (const [sku, qty] of units) out.set(sku, qty / days);
  return out;
}

export const INVENTORY_METRICS: MetricDef[] = [
  {
    id: 'inv.on_hand_units',
    kind: 'metric',
    label: 'Units on hand',
    group: 'Inventory',
    description: 'Total quantity across all locations for active items.',
    module: INV,
    requires: ['inventory'],
    unit: 'count',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.inventory ? sum(itemStock(bag).map((r) => r.qty)) : null),
  },
  {
    id: 'inv.on_hand_value',
    kind: 'metric',
    label: 'Inventory value',
    group: 'Inventory',
    description: 'On-hand quantity × standard cost.',
    module: INV,
    requires: ['inventory'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.inventory ? sum(itemStock(bag).map((r) => r.valueCents)) : null),
  },
  {
    id: 'inv.skus_tracked',
    kind: 'metric',
    label: 'SKUs stocked',
    group: 'Inventory',
    description: 'Active items with an inventory record.',
    module: INV,
    requires: ['inventory'],
    unit: 'count',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.inventory ? itemStock(bag).length : null),
  },
  {
    id: 'inv.below_reorder',
    kind: 'metric',
    label: 'Items below reorder point',
    group: 'Inventory',
    description: 'Active items whose total on hand is at or under their reorder point.',
    module: INV,
    requires: ['inventory'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.inventory ? belowReorder(itemStock(bag)).length : null),
  },
  {
    id: 'inv.stockouts',
    kind: 'metric',
    label: 'Stock-outs',
    group: 'Inventory',
    description: 'Active items with zero or negative quantity on hand.',
    module: INV,
    requires: ['inventory'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.inventory ? itemStock(bag).filter((r) => r.qty <= 0).length : null),
  },
  {
    id: 'inv.reorder_value',
    kind: 'metric',
    label: 'Replenishment needed',
    group: 'Inventory',
    description: 'Reorder quantity × standard cost for every item below its reorder point.',
    module: INV,
    requires: ['inventory'],
    unit: 'currency',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      if (!bag.inventory) return null;
      const costBy = new Map<string, number>();
      for (const level of bag.inventory) costBy.set(level.item_id, level.item?.std_cost_cents ?? 0);
      return sum(belowReorder(itemStock(bag)).map((r) => (r.reorderQty ?? 0) * (costBy.get(r.itemId) ?? 0)));
    },
  },
  {
    id: 'inv.days_of_cover',
    kind: 'metric',
    label: 'Days of cover',
    group: 'Inventory',
    description: 'Units on hand divided by average daily units sold in the window, across SKUs that sold.',
    module: INV,
    requires: ['inventory', 'orders'],
    unit: 'days',
    direction: 'neutral',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.inventory || !bag.orders) return null;
      const demand = dailyDemandBySku(bag, start, end);
      const stock = itemStock(bag).filter((r) => demand.has(r.sku));
      const dailyUnits = sum(stock.map((r) => demand.get(r.sku) ?? 0));
      return dailyUnits === 0 ? null : sum(stock.map((r) => Math.max(0, r.qty))) / dailyUnits;
    },
    available: (bag) =>
      bag.inventory && bag.orders && !bag.orders.orders.some((o) => o.lines.some((l) => l.sku))
        ? 'Needs order line items with SKUs to measure demand.'
        : null,
  },
];

export const INVENTORY_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'inv.value_by_location',
    kind: 'breakdown',
    label: 'Inventory value by location',
    group: 'Inventory',
    description: 'On-hand value per warehouse / bin.',
    module: INV,
    requires: ['inventory'],
    unit: 'currency',
    slices: (bag) =>
      toSlices(
        groupSum(
          (bag.inventory ?? []).filter((l) => l.item?.is_active !== false),
          (l) => l.location?.name ?? 'Unknown location',
          (l) => l.quantity * (l.item?.std_cost_cents ?? 0),
        ),
      ),
  },
  {
    id: 'inv.units_by_kind',
    kind: 'breakdown',
    label: 'Units by item type',
    group: 'Inventory',
    description: 'On-hand quantity split by item kind (finished good, component, …).',
    module: INV,
    requires: ['inventory'],
    unit: 'count',
    slices: (bag) => toSlices(groupSum(itemStock(bag), (r) => titleCase(r.kind ?? 'other'), (r) => r.qty)),
  },
  {
    id: 'inv.value_by_item',
    kind: 'breakdown',
    label: 'Inventory value by item',
    group: 'Inventory',
    description: 'Which SKUs hold the most cash.',
    module: INV,
    requires: ['inventory'],
    unit: 'currency',
    slices: (bag) => toSlices(groupSum(itemStock(bag), (r) => `${r.sku} · ${r.name}`, (r) => r.valueCents)),
  },
];

export const INVENTORY_TABLES: TableDef[] = [
  {
    id: 'inv.reorder_table',
    kind: 'table',
    label: 'Reorder list',
    group: 'Inventory',
    description: 'Items at or below their reorder point.',
    module: INV,
    requires: ['inventory'],
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Item' },
      { key: 'qty', label: 'On hand', unit: 'count' },
      { key: 'reorder_point', label: 'Reorder at', unit: 'count' },
      { key: 'shortfall', label: 'Shortfall', unit: 'count' },
      { key: 'reorder_qty', label: 'Reorder qty', unit: 'count' },
    ],
    defaultColumns: ['sku', 'name', 'qty', 'reorder_point', 'reorder_qty'],
    defaultSort: { key: 'shortfall', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) =>
      belowReorder(itemStock(bag)).map((r) => ({
        key: r.itemId,
        href: '/inventory/levels',
        cells: {
          sku: r.sku,
          name: r.name,
          qty: r.qty,
          reorder_point: r.reorderPoint,
          shortfall: (r.reorderPoint ?? 0) - r.qty,
          reorder_qty: r.reorderQty,
        },
      })),
  },
  {
    id: 'inv.stock_by_location',
    kind: 'table',
    label: 'Stock by location',
    group: 'Inventory',
    description: 'Items, units and value held at each location.',
    module: INV,
    requires: ['inventory'],
    columns: [
      { key: 'location', label: 'Location' },
      { key: 'items', label: 'Items', unit: 'count' },
      { key: 'units', label: 'Units', unit: 'count' },
      { key: 'value', label: 'Value', unit: 'currency' },
    ],
    defaultSort: { key: 'value', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) => {
      const acc = new Map<string, { items: Set<string>; units: number; value: number }>();
      for (const l of (bag.inventory ?? []).filter((x) => x.item?.is_active !== false)) {
        const name = l.location?.name ?? 'Unknown location';
        const row = acc.get(name) ?? { items: new Set<string>(), units: 0, value: 0 };
        row.items.add(l.item_id);
        row.units += l.quantity;
        row.value += l.quantity * (l.item?.std_cost_cents ?? 0);
        acc.set(name, row);
      }
      return Array.from(acc.entries()).map(([location, r]) => ({
        key: location,
        cells: { location, items: r.items.size, units: r.units, value: r.value },
      }));
    },
  },
  {
    id: 'inv.top_value_items',
    kind: 'table',
    label: 'Highest-value stock',
    group: 'Inventory',
    description: 'SKUs ranked by on-hand value.',
    module: INV,
    requires: ['inventory'],
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Item' },
      { key: 'qty', label: 'On hand', unit: 'count' },
      { key: 'value', label: 'Value', unit: 'currency' },
    ],
    defaultSort: { key: 'value', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) =>
      itemStock(bag).map((r) => ({
        key: r.itemId,
        cells: { sku: r.sku, name: r.name, qty: r.qty, value: r.valueCents },
      })),
  },
];

// ---------------------------------------------------------------------------
// Procurement
// ---------------------------------------------------------------------------

const PROC = 'procurement';
const PO_OPEN = new Set(['internal_po', 'ordered', 'in_production', 'shipped', 'partially_received']);

function openPos(bag: DatasetBag): PurchaseOrderLite[] {
  return (bag.purchase_orders ?? []).filter((po) => PO_OPEN.has(po.status));
}

function placedDay(po: PurchaseOrderLite): string {
  return (po.ordered_at ?? po.created_at).slice(0, 10);
}

function deliveredIn(bag: DatasetBag, start: string, end: string): PurchaseOrderLite[] {
  return (bag.purchase_orders ?? []).filter((po) => po.delivered_at && inWindow(po.delivered_at, start, end));
}

function isLate(po: PurchaseOrderLite, today = todayIso()): boolean {
  return PO_OPEN.has(po.status) && !!po.expected_at && po.expected_at.slice(0, 10) < today;
}

export const PROCUREMENT_METRICS: MetricDef[] = [
  {
    id: 'proc.open_pos',
    kind: 'metric',
    label: 'Open purchase orders',
    group: 'Procurement',
    description: 'POs placed but not fully received.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'count',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.purchase_orders ? openPos(bag).length : null),
  },
  {
    id: 'proc.open_po_value',
    kind: 'metric',
    label: 'Committed spend',
    group: 'Procurement',
    description: 'Value of open purchase orders — cash already committed to vendors.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.purchase_orders ? sum(openPos(bag).map((po) => po.total_cents)) : null),
  },
  {
    id: 'proc.late_pos',
    kind: 'metric',
    label: 'Late purchase orders',
    group: 'Procurement',
    description: 'Open POs past their expected date.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.purchase_orders ? bag.purchase_orders.filter((po) => isLate(po)).length : null),
  },
  {
    id: 'proc.po_spend',
    kind: 'metric',
    label: 'Purchase order spend',
    group: 'Procurement',
    description: 'Value of POs placed (cancelled excluded), by order date.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'day',
    agg: 'sum',
    series: (bag) =>
      dailySeries(
        (bag.purchase_orders ?? []).filter((po) => po.status !== 'cancelled'),
        placedDay,
        (po) => po.total_cents,
      ),
    nonNegative: true,
  },
  {
    id: 'proc.pos_placed',
    kind: 'metric',
    label: 'Purchase orders placed',
    group: 'Procurement',
    description: 'Number of POs placed, by order date.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'count',
    direction: 'neutral',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount((bag.purchase_orders ?? []).filter((po) => po.status !== 'cancelled'), placedDay),
    nonNegative: true,
  },
  {
    id: 'proc.on_time_receipt',
    kind: 'metric',
    label: 'On-time receipt rate',
    group: 'Procurement',
    description: 'Share of POs delivered in the window that arrived by their expected date.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'percent',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.purchase_orders) return null;
      const delivered = deliveredIn(bag, start, end).filter((po) => po.expected_at);
      if (delivered.length === 0) return null;
      const onTime = delivered.filter((po) => po.delivered_at!.slice(0, 10) <= po.expected_at!.slice(0, 10));
      return onTime.length / delivered.length;
    },
  },
  {
    id: 'proc.lead_time',
    kind: 'metric',
    label: 'Average vendor lead time',
    group: 'Procurement',
    description: 'Days from order to delivery for POs delivered in the window.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'days',
    direction: 'down',
    grain: 'window',
    agg: 'avg',
    windowValue: (bag, start, end) => {
      if (!bag.purchase_orders) return null;
      const spans = deliveredIn(bag, start, end)
        .map((po) => daysBetween(po.ordered_at ?? po.created_at, po.delivered_at))
        .filter((d): d is number => d != null && d >= 0);
      return avg(spans);
    },
  },
];

export const PROCUREMENT_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'proc.spend_by_vendor',
    kind: 'breakdown',
    label: 'PO spend by vendor',
    group: 'Procurement',
    description: 'Value of POs placed in the window per vendor.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          (bag.purchase_orders ?? []).filter((po) => po.status !== 'cancelled' && inWindow(placedDay(po), start, end)),
          (po) => po.vendorName,
          (po) => po.total_cents,
        ),
      ),
  },
  {
    id: 'proc.open_value_by_vendor',
    kind: 'breakdown',
    label: 'Committed spend by vendor',
    group: 'Procurement',
    description: 'Open PO value per vendor.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'currency',
    slices: (bag) => toSlices(groupSum(openPos(bag), (po) => po.vendorName, (po) => po.total_cents)),
  },
  {
    id: 'proc.pos_by_status',
    kind: 'breakdown',
    label: 'Purchase orders by status',
    group: 'Procurement',
    description: 'Where every open PO sits in the pipeline.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'count',
    slices: (bag) => toSlices(groupCount(openPos(bag), (po) => titleCase(po.status))),
  },
];

export const PROCUREMENT_TABLES: TableDef[] = [
  {
    id: 'proc.open_pos_table',
    kind: 'table',
    label: 'Open purchase orders',
    group: 'Procurement',
    description: 'Every PO still in flight, with its expected date.',
    module: PROC,
    requires: ['purchase_orders'],
    columns: [
      { key: 'po', label: 'PO' },
      { key: 'vendor', label: 'Vendor' },
      { key: 'status', label: 'Status' },
      { key: 'ordered', label: 'Ordered', date: true },
      { key: 'expected', label: 'Expected', date: true },
      { key: 'days_late', label: 'Days late', unit: 'days' },
      { key: 'total', label: 'Total', unit: 'currency' },
    ],
    defaultColumns: ['po', 'vendor', 'status', 'expected', 'total'],
    defaultSort: { key: 'expected', dir: 'asc' },
    defaultLimit: 8,
    rows: (bag) => {
      const today = todayIso();
      return openPos(bag).map((po) => ({
        key: po.id,
        href: '/procurement/purchase-orders',
        cells: {
          po: po.po_number,
          vendor: po.vendorName,
          status: titleCase(po.status),
          ordered: po.ordered_at?.slice(0, 10) ?? null,
          expected: po.expected_at?.slice(0, 10) ?? null,
          days_late: isLate(po, today) ? Math.floor(daysBetween(po.expected_at, today) ?? 0) : null,
          total: po.total_cents,
        },
      }));
    },
  },
  {
    id: 'proc.vendor_summary',
    kind: 'table',
    label: 'Vendor scorecard',
    group: 'Procurement',
    description: 'Spend, on-time rate and lead time per vendor for POs in the window.',
    module: PROC,
    requires: ['purchase_orders'],
    columns: [
      { key: 'vendor', label: 'Vendor' },
      { key: 'pos', label: 'POs', unit: 'count' },
      { key: 'spend', label: 'Spend', unit: 'currency' },
      { key: 'on_time', label: 'On time', unit: 'percent' },
      { key: 'lead', label: 'Lead time', unit: 'days' },
    ],
    defaultSort: { key: 'spend', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { pos: number; spend: number; delivered: number; onTime: number; leads: number[] }>();
      for (const po of bag.purchase_orders ?? []) {
        if (po.status === 'cancelled') continue;
        const placed = inWindow(placedDay(po), start, end);
        const delivered = !!po.delivered_at && inWindow(po.delivered_at, start, end);
        if (!placed && !delivered) continue;
        const row = acc.get(po.vendorName) ?? { pos: 0, spend: 0, delivered: 0, onTime: 0, leads: [] };
        if (placed) {
          row.pos += 1;
          row.spend += po.total_cents;
        }
        if (delivered) {
          if (po.expected_at) {
            row.delivered += 1;
            if (po.delivered_at!.slice(0, 10) <= po.expected_at.slice(0, 10)) row.onTime += 1;
          }
          const lead = daysBetween(po.ordered_at ?? po.created_at, po.delivered_at);
          if (lead != null && lead >= 0) row.leads.push(lead);
        }
        acc.set(po.vendorName, row);
      }
      return Array.from(acc.entries()).map(([vendor, r]) => ({
        key: vendor,
        cells: {
          vendor,
          pos: r.pos,
          spend: r.spend,
          on_time: r.delivered === 0 ? null : r.onTime / r.delivered,
          lead: avg(r.leads),
        },
      }));
    },
  },
];

export const PROCUREMENT_FORECASTS: ForecastSourceDef[] = [
  {
    id: 'fc.po_spend',
    kind: 'forecast',
    label: 'Purchase order spend',
    group: 'Procurement',
    description: 'Monthly PO spend projected forward.',
    module: PROC,
    requires: ['purchase_orders'],
    unit: 'currency',
    nonNegative: true,
    history: (bag) =>
      monthlySeries(
        (bag.purchase_orders ?? []).filter((po) => po.status !== 'cancelled'),
        placedDay,
        (po) => po.total_cents,
      ),
  },
];

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

const PROJ = 'projects';

function openTasks(bag: DatasetBag): TaskLite[] {
  return (bag.tasks ?? []).filter((t) => !t.is_completed);
}

function overdueTasks(bag: DatasetBag, today = todayIso()): TaskLite[] {
  return openTasks(bag).filter((t) => t.due_date && t.due_date.slice(0, 10) < today);
}

function completedIn(bag: DatasetBag, start: string, end: string): TaskLite[] {
  return (bag.tasks ?? []).filter((t) => t.is_completed && t.completed_at && inWindow(t.completed_at, start, end));
}

export const PROJECT_METRICS: MetricDef[] = [
  {
    id: 'proj.open_tasks',
    kind: 'metric',
    label: 'Open tasks',
    group: 'Projects',
    description: 'Tasks not yet completed.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.tasks ? openTasks(bag).length : null),
  },
  {
    id: 'proj.overdue_tasks',
    kind: 'metric',
    label: 'Overdue tasks',
    group: 'Projects',
    description: 'Open tasks past their due date.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.tasks ? overdueTasks(bag).length : null),
  },
  {
    id: 'proj.in_progress',
    kind: 'metric',
    label: 'Tasks in progress',
    group: 'Projects',
    description: 'Open tasks someone is actively working.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => (bag.tasks ? openTasks(bag).filter((t) => t.status === 'in_progress').length : null),
  },
  {
    id: 'proj.tasks_completed',
    kind: 'metric',
    label: 'Tasks completed',
    group: 'Projects',
    description: 'Tasks marked done, by completion date.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount((bag.tasks ?? []).filter((t) => t.is_completed && t.completed_at), (t) => t.completed_at),
    nonNegative: true,
  },
  {
    id: 'proj.tasks_created',
    kind: 'metric',
    label: 'Tasks created',
    group: 'Projects',
    description: 'New tasks, by creation date.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    direction: 'neutral',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailyCount(bag.tasks ?? [], (t) => t.created_at),
    nonNegative: true,
  },
  {
    id: 'proj.on_time_completion',
    kind: 'metric',
    label: 'On-time completion',
    group: 'Projects',
    description: 'Share of tasks completed in the window that finished by their due date.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'percent',
    direction: 'up',
    grain: 'window',
    agg: 'sum',
    windowValue: (bag, start, end) => {
      if (!bag.tasks) return null;
      const done = completedIn(bag, start, end).filter((t) => t.due_date);
      if (done.length === 0) return null;
      return done.filter((t) => t.completed_at!.slice(0, 10) <= t.due_date!.slice(0, 10)).length / done.length;
    },
  },
  {
    id: 'proj.cycle_days',
    kind: 'metric',
    label: 'Task cycle time',
    group: 'Projects',
    description: 'Average days from creation to completion for tasks completed in the window.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'days',
    direction: 'down',
    grain: 'window',
    agg: 'avg',
    windowValue: (bag, start, end) => {
      if (!bag.tasks) return null;
      return avg(
        completedIn(bag, start, end)
          .map((t) => daysBetween(t.created_at, t.completed_at))
          .filter((d): d is number => d != null && d >= 0),
      );
    },
  },
];

export const PROJECT_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'proj.open_by_project',
    kind: 'breakdown',
    label: 'Open tasks by project',
    group: 'Projects',
    description: 'Where the open work sits.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    slices: (bag) => toSlices(groupCount(openTasks(bag), (t) => t.projectName ?? 'No project')),
  },
  {
    id: 'proj.overdue_by_project',
    kind: 'breakdown',
    label: 'Overdue tasks by project',
    group: 'Projects',
    description: 'Which projects are slipping.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    slices: (bag) => toSlices(groupCount(overdueTasks(bag), (t) => t.projectName ?? 'No project')),
  },
  {
    id: 'proj.tasks_by_status',
    kind: 'breakdown',
    label: 'Open tasks by status',
    group: 'Projects',
    description: 'Not started / waiting / in progress.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    slices: (bag) => toSlices(groupCount(openTasks(bag), (t) => titleCase(t.status))),
  },
  {
    id: 'proj.completed_by_project',
    kind: 'breakdown',
    label: 'Completed tasks by project',
    group: 'Projects',
    description: 'Where work got finished in the window.',
    module: PROJ,
    requires: ['tasks'],
    unit: 'count',
    slices: (bag, start, end) => toSlices(groupCount(completedIn(bag, start, end), (t) => t.projectName ?? 'No project')),
  },
];

export const PROJECT_TABLES: TableDef[] = [
  {
    id: 'proj.overdue_table',
    kind: 'table',
    label: 'Overdue tasks',
    group: 'Projects',
    description: 'Open tasks past due, most overdue first.',
    module: PROJ,
    requires: ['tasks'],
    columns: [
      { key: 'task', label: 'Task' },
      { key: 'project', label: 'Project' },
      { key: 'due', label: 'Due', date: true },
      { key: 'days_late', label: 'Days late', unit: 'days' },
      { key: 'status', label: 'Status' },
    ],
    defaultSort: { key: 'days_late', dir: 'desc' },
    defaultLimit: 8,
    rows: (bag) => {
      const today = todayIso();
      return overdueTasks(bag, today).map((t) => ({
        key: t.id,
        href: `/projects/${t.project_id}`,
        cells: {
          task: t.name,
          project: t.projectName ?? '—',
          due: t.due_date,
          days_late: Math.floor(daysBetween(t.due_date, today) ?? 0),
          status: titleCase(t.status),
        },
      }));
    },
  },
  {
    id: 'proj.project_summary',
    kind: 'table',
    label: 'Project summary',
    group: 'Projects',
    description: 'Open, overdue and recently completed tasks per project.',
    module: PROJ,
    requires: ['tasks'],
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'open', label: 'Open', unit: 'count' },
      { key: 'overdue', label: 'Overdue', unit: 'count' },
      { key: 'completed', label: 'Completed', unit: 'count' },
      { key: 'done_share', label: 'Done', unit: 'percent' },
    ],
    defaultSort: { key: 'open', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const today = todayIso();
      const acc = new Map<string, { id: string; open: number; overdue: number; completed: number; total: number }>();
      for (const t of bag.tasks ?? []) {
        const name = t.projectName ?? 'No project';
        const row = acc.get(name) ?? { id: t.project_id, open: 0, overdue: 0, completed: 0, total: 0 };
        row.total += 1;
        if (!t.is_completed) {
          row.open += 1;
          if (t.due_date && t.due_date.slice(0, 10) < today) row.overdue += 1;
        } else if (t.completed_at && inWindow(t.completed_at, start, end)) {
          row.completed += 1;
        }
        acc.set(name, row);
      }
      return Array.from(acc.entries()).map(([project, r]) => ({
        key: r.id,
        href: `/projects/${r.id}`,
        cells: {
          project,
          open: r.open,
          overdue: r.overdue,
          completed: r.completed,
          done_share: r.total === 0 ? null : (r.total - r.open) / r.total,
        },
      }));
    },
  },
];
